import React, { forwardRef, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { InlineChartCard, parseChartDataFromTable } from "../../AiComponents/AiChart";

function normalize(s) {
    return String(s ?? "").trim();
}

function includesAllTokens(line, tokens) {
    const text = normalize(line).toLowerCase();
    return (tokens || []).every((token) => text.includes(normalize(token).toLowerCase()));
}

function isSeparatorRow(line) {
    const text = normalize(line);
    return text.includes("|") && /-+/.test(text);
}

function isTableRow(line) {
    return normalize(line).includes("|");
}

function splitMarkdownWithCharts(content, chartConfigs) {
    const markdown = String(content || "");
    const lines = markdown.split("\n");
    const configs = Array.isArray(chartConfigs) ? chartConfigs : [];
    const segments = [];
    const usedConfigIndexes = new Set();
    let cursor = 0;

    for (let i = 0; i < lines.length; i++) {
        const configIndex = configs.findIndex((cfg, idx) => {
            if (usedConfigIndexes.has(idx)) return false;
            return includesAllTokens(lines[i], cfg?.headerTokens || []);
        });

        if (configIndex < 0) continue;
        if (!isSeparatorRow(lines[i + 1] || "")) continue;

        let j = i + 2;
        while (j < lines.length && isTableRow(lines[j])) {
            j += 1;
        }

        const tableBlock = lines.slice(i, j).join("\n");
        const cfg = configs[configIndex];
        const data = parseChartDataFromTable(tableBlock, cfg?.headerTokens || []);

        if (!data.length) continue;

        const before = lines.slice(cursor, i).join("\n");
        if (before.trim()) {
            segments.push({ type: "markdown", content: before });
        }

        segments.push({
            type: "chart",
            kind: cfg?.kind,
            title: cfg?.title,
            valueLabel: cfg?.valueLabel,
            data,
        });

        usedConfigIndexes.add(configIndex);
        cursor = j;
        i = j - 1;
    }

    const tail = lines.slice(cursor).join("\n");
    if (tail.trim()) {
        segments.push({ type: "markdown", content: tail });
    }

    if (!segments.length) {
        segments.push({ type: "markdown", content: markdown });
    }

    return segments;
}

function MarkdownChunk({ content }) {
    let h3Count = 0;

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
            components={{
                ul: (props) => <ul className="list-disc pl-6 my-2" {...props} />,
                ol: (props) => <ol className="list-decimal pl-6 my-2" {...props} />,
                li: (props) => <li className="text-[12px] my-1" {...props} />,
                h2: (props) => <h2 className="text-[20px] font-bold mt-7 mb-5 text-center" {...props} />,
                h3: (props) => {
                    h3Count += 1;
                    return (
                        <h3
                            className={h3Count === 1 ? "text-[14px] font-semibold my-2 mt-0" : "text-[14px] font-semibold my-2 mt-[2rem]"}
                            {...props}
                        />
                    );
                },
                p: (props) => <p className="" {...props} />,
                table: (props) => <table className="my-3 w-full border-collapse" {...props} />,
                th: (props) => <th className="border px-2 py-1 text-left text-[12px] font-semibold" {...props} />,
                td: (props) => <td className="border px-2 py-1 text-[12px]" {...props} />,
            }}
        >
            {content || ""}
        </ReactMarkdown>
    );
}

function groupSegments(segments) {
    const grouped = [];

    segments.forEach((segment) => {
        const prev = grouped[grouped.length - 1];

        if (segment.type === "chart" && prev?.type === "chartRow" && prev.items.length < 2) {
            prev.items.push(segment);
            return;
        }

        if (segment.type === "chart") {
            grouped.push({ type: "chartRow", items: [segment] });
            return;
        }

        grouped.push(segment);
    });

    return grouped;
}

export const MarkdownViewer = forwardRef(function MarkdownViewer(
    { content, className, chartConfigs },
    ref
) {
    const segments = useMemo(
        () => groupSegments(splitMarkdownWithCharts(content, chartConfigs)),
        [content, chartConfigs]
    );

    return (
        <div ref={ref} className={className || ""}>
            {segments.map((segment, index) => {
                if (segment.type === "chartRow") {
                    return (
                        <div key={`chart-row-${index}`} className="my-3 grid grid-cols-2 gap-4">
                            {segment.items.map((item, itemIndex) => (
                                <InlineChartCard
                                    key={`chart-${index}-${itemIndex}`}
                                    kind={item.kind}
                                    title={item.title}
                                    data={item.data}
                                    valueLabel={item.valueLabel}
                                />
                            ))}
                            {segment.items.length === 1 ? <div /> : null}
                        </div>
                    );
                }

                return <MarkdownChunk key={`md-${index}`} content={segment.content} />;
            })}
        </div>
    );
});
